interface CompletedValue {
  expiresAt: number;
  value: unknown;
}

interface Flight<T> {
  invalidated: boolean;
  promise: Promise<T>;
}

const completedValues = new Map<string, CompletedValue>();
const flights = new Map<string, Flight<unknown>>();

/**
 * Return a fresh cached value or coalesce concurrent work for the same key.
 * Fetchers start in a microtask so the owning Promise is installed first.
 */
export function cachedRequest<T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>,
): Promise<T> {
  const completed = completedValues.get(key);
  if (completed) {
    if (Date.now() < completed.expiresAt) {
      return Promise.resolve(completed.value as T);
    }
    completedValues.delete(key);
  }

  const existing = flights.get(key) as Flight<T> | undefined;
  if (existing) return existing.promise;

  const owner = { invalidated: false } as Flight<T>;
  const promise = Promise.resolve()
    .then(fetcher)
    .then((value) => {
      if (!owner.invalidated && flights.get(key) === owner) {
        completedValues.set(key, { expiresAt: Date.now() + ttlMs, value });
      }
      return value;
    })
    .finally(() => {
      if (flights.get(key) === owner) flights.delete(key);
    });
  owner.promise = promise;
  flights.set(key, owner as Flight<unknown>);
  return promise;
}

/** Invalidate completed values and detach matching in-flight owners. */
export function invalidateRequestCache(prefix?: string): void {
  const matches = (key: string) => prefix === undefined || key.startsWith(prefix);

  for (const key of completedValues.keys()) {
    if (matches(key)) completedValues.delete(key);
  }
  for (const [key, flight] of flights) {
    if (!matches(key)) continue;
    flight.invalidated = true;
    flights.delete(key);
  }
}
